import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmployeeDetailModal = ({ employee, isOpen, onClose, onUploadCertificate }) => {
  const [activeTab, setActiveTab] = useState('certifications');

  if (!isOpen || !employee) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'expired':
        return 'bg-error text-error-foreground';
      case 'expiring':
        return 'bg-warning text-warning-foreground';
      case 'current':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getCompliancePercentage = () => {
    const total = employee?.certifications?.length;
    const current = employee?.certifications?.filter(cert => cert?.status === 'current')?.length;
    return Math.round((current / total) * 100);
  };

  const tabs = [
    { id: 'certifications', label: 'Certifications', icon: 'Award' },
    { id: 'history', label: 'Training History', icon: 'Clock' },
    { id: 'requirements', label: 'Requirements', icon: 'CheckSquare' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
              <span className="text-lg font-medium text-primary-foreground">
                {employee?.name?.split(' ')?.map(n => n?.[0])?.join('')}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{employee?.name}</h2>
              <p className="text-sm text-muted-foreground">{employee?.role} • {employee?.department}</p>
              <div className="flex items-center space-x-2 mt-1">
                <div className="w-20 bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      getCompliancePercentage() >= 80 ? 'bg-success' :
                      getCompliancePercentage() >= 60 ? 'bg-warning' : 'bg-error'
                    }`}
                    style={{ width: `${getCompliancePercentage()}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-foreground">
                  {getCompliancePercentage()}% Compliant
                </span>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex space-x-8 px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition-clinical ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="text-sm font-medium">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'certifications' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Current Certifications</h3>
                <Button variant="outline" iconName="Upload" iconPosition="left" onClick={() => onUploadCertificate(employee)}>
                  Upload Certificate
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {employee?.certifications?.map((cert, index) => (
                  <div key={index} className="bg-muted/30 rounded-lg p-4 border border-border">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-foreground">{cert?.name}</h4>
                        <p className="text-sm text-muted-foreground">{cert?.provider}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(cert?.status)}`}>
                        {cert?.status}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Issued:</span>
                        <span className="text-foreground">{cert?.issueDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expires:</span>
                        <span className="text-foreground">{cert?.expiryDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Certificate ID:</span>
                        <span className="text-foreground font-mono text-xs">{cert?.certificateId}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 mt-3">
                      <Button variant="ghost" size="sm" iconName="Eye">
                        View
                      </Button>
                      <Button variant="ghost" size="sm" iconName="Download">
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Training History</h3>
              <div className="space-y-3">
                {employee?.trainingHistory?.map((training, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 bg-muted/30 rounded-lg">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="GraduationCap" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{training?.course}</h4>
                      <p className="text-sm text-muted-foreground">{training?.provider}</p>
                      <p className="text-xs text-muted-foreground">Completed on {training?.completionDate}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-success">Passed</span>
                      <p className="text-xs text-muted-foreground">Score: {training?.score}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'requirements' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Training Requirements</h3>
              <div className="space-y-3">
                {employee?.requirements?.map((requirement, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon 
                        name={requirement?.completed ? "CheckCircle" : "Clock"} 
                        size={20} 
                        className={requirement?.completed ? "text-success" : "text-warning"}
                      />
                      <div>
                        <h4 className="font-medium text-foreground">{requirement?.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {requirement?.completed ? `Completed ${requirement?.completionDate}` : `Due ${requirement?.dueDate}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-medium ${requirement?.completed ? 'text-success' : 'text-warning'}`}>
                        {requirement?.completed ? 'Complete' : 'Pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetailModal;